import { useMutation } from "convex/react";
import { api } from "../convex/_generated/api";
import { Id } from "../convex/_generated/dataModel";
import { FileUploadMetadataSchema } from "../lib/validators/fileUpload";

/**
 * Convex file upload service
 * Handles uploading files to Convex storage and recording metadata
 */
export function useConvexUploadService() {
  const generateUploadUrl = useMutation(api.files.generateUploadUrl);
  const recordFileUpload = useMutation(api.files.recordFileUpload);

  /**
   * Upload a single file to Convex storage
   * 
   * Flow:
   * 1. Generate upload URL from Convex
   * 2. Upload file directly to Convex storage (PUT request)
   * 3. Record file metadata in database
   */
  async function uploadFile(
    file: File,
    jobId: Id<"jobs">,
    convertHeicAndHeif: boolean
  ): Promise<Id<"jobFiles">> {
    // Validate file before upload
    if (file.size > 10 * 1024 * 1024) {
      throw new Error(`File ${file.name} exceeds 10MB limit`);
    }

    // Step 1: Generate upload URL
    const uploadUrl = await generateUploadUrl({});

    // Step 2: Upload file directly to Convex storage
    const uploadResponse = await fetch(uploadUrl, {
      method: "POST",  // Convex uses POST, not PUT
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(
        `Upload failed for ${file.name}: ${uploadResponse.statusText}`
      );
    }

    // Step 3: Extract storage ID from response
    const { storageId } = (await uploadResponse.json()) as { storageId: Id<"_storage"> };

    if (!storageId) {
      throw new Error(`No storage ID returned for ${file.name}`);
    }

    // Step 4: Prepare metadata with correct Convex types
    const metadata = {
      jobId,
      storageId,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      convertHeicAndHeif,
    };

    // Step 5: Validate metadata with Zod (runtime checks only)
    // Note: We validate but use original metadata to preserve Id<> types
    const validationResult = FileUploadMetadataSchema.safeParse({
      jobId: jobId as string,
      storageId: storageId as string,
      fileName: metadata.fileName,
      fileSize: metadata.fileSize,
      mimeType: metadata.mimeType,
      convertHeicAndHeif: metadata.convertHeicAndHeif,
    });
    if (!validationResult.success) {
      const errors = validationResult.error.issues
        .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
        .join(", ");
      throw new Error(`Validation failed for ${file.name}: ${errors}`);
    }

    // Step 6: Record file upload in database (use original typed metadata)
    const fileId = await recordFileUpload(metadata);

    return fileId;
  }

  /**
   * Upload multiple files in batches to avoid rate limits
   *
   * Processes files in chunks of 30 to balance performance with stability.
   * Each batch uploads in parallel, but batches run sequentially.
   */
  async function uploadFiles(
    files: File[],
    jobId: Id<"jobs">,
    convertHeicAndHeif: boolean,
    onProgress?: (completed: number, total: number) => void
  ): Promise<Id<"jobFiles">[]> {
    const BATCH_SIZE = 30;
    const total = files.length;
    let completed = 0;
    const results: Id<"jobFiles">[] = [];

    // Split files into batches
    const batches: File[][] = [];
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      batches.push(files.slice(i, i + BATCH_SIZE));
    }

    console.log(`📦 Uploading ${total} files in ${batches.length} batches of ~${BATCH_SIZE} files each`);

    // Process each batch sequentially
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      const batchNumber = batchIndex + 1;

      console.log(`🚀 Starting batch ${batchNumber}/${batches.length} (${batch.length} files)`);

      // Upload all files in current batch in parallel
      const batchPromises = batch.map((file) => uploadFile(file, jobId, convertHeicAndHeif));

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // Update progress after batch completes
      completed += batch.length;
      if (onProgress) {
        onProgress(completed, total);
      }

      console.log(`✅ Completed batch ${batchNumber}/${batches.length} (${completed}/${total} files total)`);

      // Add small delay between batches to avoid rate limits
      // Skip delay after last batch
      if (batchIndex < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    console.log(`🎉 All ${total} files uploaded successfully`);
    return results;
  }

  return {
    uploadFile,
    uploadFiles,
  };
}

